const SUPPORTED_LANGUAGES = ['pl', 'en'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const LANGUAGE_STORAGE_KEY = 'ct_lang';

const isSupportedLanguage = (value: unknown): value is SupportedLanguage =>
  typeof value === 'string' && SUPPORTED_LANGUAGES.includes(value as SupportedLanguage);

const getStorage = (): Storage | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

const extractRegion = (locale: string | undefined): string | undefined => {
  if (!locale) {
    return undefined;
  }

  try {
    const IntlWithLocale = Intl as typeof Intl & {
      Locale?: new (tag?: string) => { region?: string | undefined };
    };

    if (typeof IntlWithLocale.Locale === 'function') {
      const intlLocale = new IntlWithLocale.Locale(locale);
      const { region } = intlLocale as { region?: string | undefined };
      if (region) {
        return region.toUpperCase();
      }
    }
  } catch {
    // ignore – fallback to regex parsing below
  }

  const match = locale.match(/[-_](?<region>[A-Za-z]{2}|\d{3})/);
  const region = match?.groups?.region ?? match?.[1];

  return region?.toUpperCase();
};

const detectRegion = (): string | undefined => {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
    return undefined;
  }

  try {
    const resolvedLocale = new Intl.DateTimeFormat().resolvedOptions().locale;
    return extractRegion(resolvedLocale);
  } catch {
    return undefined;
  }
};

const getNavigatorLanguages = (): string[] => {
  if (typeof navigator === 'undefined') {
    return [];
  }

  const list =
    Array.isArray(navigator.languages) && navigator.languages.length > 0
      ? navigator.languages
      : navigator.language
        ? [navigator.language]
        : [];

  return list.filter(Boolean).map((language) => language.toLowerCase());
};

export const getInitialLanguage = (): SupportedLanguage => {
  const storage = getStorage();
  const stored = storage?.getItem(LANGUAGE_STORAGE_KEY);

  if (isSupportedLanguage(stored)) {
    return stored;
  }

  const region = detectRegion();
  if (region === 'PL') {
    return 'pl';
  }

  const navigatorLanguages = getNavigatorLanguages();
  if (navigatorLanguages.some((language) => language.startsWith('pl'))) {
    return 'pl';
  }

  return 'en';
};

export const persistLanguage = (language: SupportedLanguage): void => {
  const storage = getStorage();
  storage?.setItem(LANGUAGE_STORAGE_KEY, language);
};

export { LANGUAGE_STORAGE_KEY, isSupportedLanguage };
